<template>
	<ArkPagination.Root
		v-if="normalizedCount > 0"
		class="pagination"
		:count="normalizedCount"
		:page="currentPage"
		:page-size="normalizedPageSize"
		:sibling-count="normalizedSiblingCount"
		:translations="translations"
		type="button"
		@update:page="handlePageChange"
	>
		<span class="range">{{ firstItem }}-{{ lastItem }} {{ rangeOfLabel }} {{ normalizedCount }}</span>

		<ArkPagination.PrevTrigger class="trigger">
			<span aria-hidden="true">‹</span>
		</ArkPagination.PrevTrigger>

		<ArkPagination.Context v-slot="{ pages }">
			<template v-for="(item, index) in pages" :key="item.type === 'page' ? item.value : `ellipsis-${index}`">
				<ArkPagination.Item v-if="item.type === 'page'" class="item" type="page" :value="item.value">
					{{ item.value }}
				</ArkPagination.Item>

				<ArkPagination.Ellipsis v-else class="ellipsis" :index="index">…</ArkPagination.Ellipsis>
			</template>
		</ArkPagination.Context>

		<ArkPagination.NextTrigger class="trigger">
			<span aria-hidden="true">›</span>
		</ArkPagination.NextTrigger>
	</ArkPagination.Root>
</template>

<script setup lang="ts">
import { Pagination as ArkPagination } from "@ark-ui/vue"
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { ACTIVITY_PAGE, ACTIVITY_PAGE_SIZE, ACTIVITY_SIBLING_COUNT } from "@/components/constants"

type PageChangeDetails = {
	page: number
	pageSize: number
	offset: number
}

const props = withDefaults(
	defineProps<{
		count: number
		page?: number
		pageSize?: number
		siblingCount?: number
		ariaLabel?: string
		previousLabel?: string
		nextLabel?: string
		ofLabel?: string
	}>(),
	{
		page: ACTIVITY_PAGE,
		pageSize: ACTIVITY_PAGE_SIZE,
		siblingCount: ACTIVITY_SIBLING_COUNT,
	}
)

const emit = defineEmits<{
	"update:page": [page: number]
	"page-change": [details: PageChangeDetails]
}>()

const { t } = useI18n()
const normalizedCount = computed(() => Math.max(0, Math.floor(props.count)))
const normalizedPageSize = computed(() => Math.max(1, Math.floor(props.pageSize)))
const normalizedSiblingCount = computed(() => Math.max(0, Math.floor(props.siblingCount)))
const totalPages = computed(() => Math.max(1, Math.ceil(normalizedCount.value / normalizedPageSize.value)))
const currentPage = computed(() => Math.min(Math.max(1, Math.floor(props.page)), totalPages.value))
const firstItem = computed(() => (currentPage.value - 1) * normalizedPageSize.value + 1)
const lastItem = computed(() => Math.min(currentPage.value * normalizedPageSize.value, normalizedCount.value))
const rangeOfLabel = computed(() => props.ofLabel ?? t("pagination.of"))
const translations = computed(() => ({
	rootLabel: props.ariaLabel ?? t("pagination.label"),
	prevTriggerLabel: props.previousLabel ?? t("pagination.previous"),
	nextTriggerLabel: props.nextLabel ?? t("pagination.next"),
}))

function handlePageChange(page: number): void {
	const details = {
		page,
		pageSize: normalizedPageSize.value,
		offset: (page - 1) * normalizedPageSize.value,
	}

	emit("update:page", page)
	emit("page-change", details)
}
</script>

<style scoped>
.pagination {
	display: inline-flex;
	align-items: center;
	justify-content: flex-end;
	gap: var(--spacing-xs);
	width: 100%;
	font-family: var(--font-family-primary);
	font-size: var(--font-size-small);
}

.range {
	margin-right: auto;
	color: var(--neutral-600);
	white-space: nowrap;
}

.trigger,
.item,
.ellipsis {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: var(--size-button);
	min-width: var(--size-button);
	line-height: var(--line-height-tight);
}

.trigger,
.item {
	padding: 0 var(--spacing-sm);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-sm);
	background-color: var(--neutral-0);
	color: var(--neutral-900);
	font: inherit;
	cursor: pointer;
	transition:
		background-color 0.2s ease,
		border-color 0.2s ease,
		color 0.2s ease;

	&:hover:not([disabled], [data-selected]) {
		border-color: var(--primary-green);
		color: var(--primary-green);
	}

	&:focus-visible {
		outline: 2px solid var(--primary-green);
		outline-offset: 2px;
	}
}

.trigger {
	&[data-disabled],
	&[disabled] {
		cursor: not-allowed;
		opacity: 0.45;
	}

	span {
		font-size: var(--font-size-base);
	}
}

.item[data-selected] {
	border-color: var(--primary-green);
	background-color: var(--primary-green);
	color: var(--neutral-0);
}

.ellipsis {
	color: var(--neutral-600);
	user-select: none;
}
</style>
